<template>
  <div class="user-management">
    <div class="header">
      <h1>User Management</h1>
      <div class="controls">
        <input 
          type="text" 
          v-model="searchQuery" 
          placeholder="Search users..." 
          class="search-input"
        />
        <button 
          class="add-user-btn"
          @click="showAddUserModal = true"
        >
          Add New User
        </button>
      </div>
    </div>
    
    <div v-if="isLoading" class="loading">Loading users...</div>
    <div v-else-if="error" class="error">{{ error }}</div>
    <div v-else class="table-container">
      <table class="data-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Customer</th>
            <th>Role</th>
            <th>Status</th>
            <th>Created On</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in filteredUsers" :key="user.id">
            <td>
              <span v-if="!editingUser || editingUser.id !== user.id">{{ user.email }}</span>
              <input 
                v-else 
                type="email" 
                v-model="editingUser.email"
                class="inline-input"
                disabled
              />
            </td>
            <td>
              <span v-if="!editingUser || editingUser.id !== user.id">{{ user.customer }}</span>
              <input 
                v-else 
                type="text" 
                v-model="editingUser.customer"
                class="inline-input"
              />
            </td>
            <td>
              <span v-if="!editingUser || editingUser.id !== user.id" :class="['role-badge', getRoleClass(user.role)]">
                {{ user.role }}
              </span>
              <select 
                v-else 
                v-model="editingUser.role"
                class="inline-input"
              >
                <option value="Admin">Admin</option>
                <option value="Job Manager">Job Manager</option>
                <option value="Customer">Customer</option>
              </select>
            </td>
            <td>
              <span v-if="!editingUser || editingUser.id !== user.id" :class="['status-badge', user.activated ? 'status-active' : 'status-inactive']">
                {{ user.activated ? 'Active' : 'Inactive' }}
              </span>
              <select 
                v-else 
                v-model="editingUser.activated"
                class="inline-input"
              >
                <option :value="true">Active</option>
                <option :value="false">Inactive</option>
              </select>
            </td>
            <td>{{ formatDate(user.CreatedAt) }}</td>
            <td>
              <div v-if="editingUser && editingUser.id === user.id">
                <button @click="saveUser(user)" class="btn btn-success btn-sm me-2">
                  <i class="fas fa-save"></i>
                </button>
                <button @click="cancelEditing(user)" class="btn btn-secondary btn-sm">
                  <i class="fas fa-times"></i>
                </button>
              </div>
              <div v-else>
                <button @click="startEditing(user)" class="btn btn-primary btn-sm me-2">
                  <i class="fas fa-edit"></i>
                </button>
                <button @click="deleteUser(user.id)" class="btn btn-danger btn-sm">
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Add User Modal -->
    <div v-if="showAddUserModal" class="modal-overlay">
      <div class="modal-content">
        <h2 class="modal-title">Add New User</h2>
        <form @submit.prevent="addUser" class="user-form">
          <div class="form-group">
            <label>Email</label>
            <input 
              type="email" 
              v-model="userForm.email" 
              required
              class="form-input"
            />
          </div>
          
          <div class="form-group">
            <label>Customer</label>
            <input 
              type="text" 
              v-model="userForm.customer" 
              required
              class="form-input"
            />
          </div>
          
          <div class="form-group">
            <label>Role</label>
            <select 
              v-model="userForm.role" 
              required
              class="form-input"
            >
              <option value="Admin">Admin</option>
              <option value="Job Manager">Job Manager</option>
              <option value="Customer">Customer</option>
            </select>
          </div>
          
          <div class="form-group">
            <label>Password</label>
            <input 
              type="password" 
              v-model="userForm.password" 
              required
              class="form-input"
            />
          </div>
          
          <div class="form-actions">
            <button 
              type="button" 
              class="cancel-btn"
              @click="closeModal"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              class="save-btn"
              :disabled="isSaving"
            >
              {{ isSaving ? 'Saving...' : 'Save' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'UserManagementView',
  data() {
    return {
      users: [],
      searchQuery: '',
      isLoading: false,
      error: null,
      showAddUserModal: false,
      editingUser: null,
      isSaving: false,
      userForm: {
        email: '',
        customer: '',
        role: 'Customer',
        password: '',
        activated: true
      }
    };
  },
  computed: {
    filteredUsers() {
      if (!this.searchQuery) return this.users;
      
      const query = this.searchQuery.toLowerCase();
      return this.users.filter(user => 
        user.email.toLowerCase().includes(query) ||
        user.customer.toLowerCase().includes(query) ||
        user.role.toLowerCase().includes(query)
      );
    }
  },
  methods: {
    async fetchUsers() {
      this.isLoading = true;
      try {
        const response = await axios.get('/auth/users');
        this.users = response.data;
      } catch (error) {
        console.error('Error fetching users:', error);
        this.error = error.response?.data?.message || 'Failed to fetch users';
      } finally {
        this.isLoading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    getRoleClass(role) {
      const roleMap = {
        'Admin': 'role-admin',
        'Job Manager': 'role-job-manager',
        'Customer': 'role-customer'
      };
      return roleMap[role] || 'role-default';
    },
    startEditing(user) {
      this.editingUser = { ...user };
    },
    async saveUser() {
      if (!this.editingUser) return;
      
      try {
        await axios.put(`/auth/users/${this.editingUser.id}`, {
          customer: this.editingUser.customer,
          role: this.editingUser.role,
          activated: this.editingUser.activated
        });
        
        // Update the user in the local array
        const index = this.users.findIndex(u => u.id === this.editingUser.id);
        if (index !== -1) {
          this.users[index] = { ...this.editingUser };
        }
        
        this.editingUser = null;
      } catch (error) {
        console.error('Error saving user:', error);
        this.error = 'Failed to save user changes. Please try again.';
      }
    },
    cancelEditing() {
      this.editingUser = null;
    },
    async deleteUser(userId) {
      if (!confirm('Are you sure you want to delete this user?')) {
        return;
      }
      try {
        await axios.delete(`/auth/users/${userId}`);
        await this.fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        this.error = error.response?.data?.message || 'Failed to delete user';
      }
    },
    async addUser() {
      this.isSaving = true;
      try {
        const userData = {
          email: this.userForm.email,
          password: this.userForm.password,
          customer: this.userForm.customer,
          role: this.userForm.role,
          activated: true // New users are activated by default
        };
        
        await axios.post('/auth/users', userData);
        await this.fetchUsers();
        this.closeModal();
      } catch (error) {
        console.error('Error adding user:', error);
        this.error = error.response?.data?.message || 'Failed to add user';
      } finally {
        this.isSaving = false;
      }
    },
    closeModal() {
      this.showAddUserModal = false;
      this.userForm = {
        email: '',
        customer: '',
        role: 'Customer',
        password: ''
      };
    }
  },
  mounted() {
    this.fetchUsers();
  }
};
</script>

<style scoped>
.user-management {
  padding: 2rem;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

h1 {
  color: var(--text-primary);
  font-size: 1.8rem;
  font-weight: 600;
}

.controls {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.search-input {
  padding: 0.5rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  width: 300px;
}

.add-user-btn {
  background-color: var(--primary-color);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
}

.add-user-btn:hover {
  background-color: var(--primary-hover);
}

.table-container {
  background: var(--background);
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th,
.data-table td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.data-table th {
  background-color: var(--background-alt);
  font-weight: 600;
  color: var(--text-primary);
}

.role-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
}

.role-admin {
  background-color: #dbeafe;
  color: #1e40af;
}

.role-job-manager {
  background-color: #fef3c7;
  color: #92400e;
}

.role-customer {
  background-color: #dcfce7;
  color: #166534;
}

.role-default {
  background-color: #f3f4f6;
  color: #374151;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-active {
  background-color: #dcfce7;
  color: #166534;
}

.status-inactive {
  background-color: #fee2e2;
  color: #991b1b;
}

.edit-btn {
  background-color: var(--primary-color);
  color: white;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  border: none;
  cursor: pointer;
  margin-right: 0.5rem;
}

.delete-btn {
  background-color: #ef4444;
  color: white;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  border: none;
  cursor: pointer;
  margin-left: 0.5rem;
}

.delete-btn:hover {
  background-color: #dc2626;
}

.save-btn {
  background-color: #10b981;
  color: white;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  border: none;
  cursor: pointer;
}

.save-btn:hover {
  background-color: #059669;
}

.cancel-btn {
  background-color: #f3f4f6;
  color: var(--text-primary);
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  border: 1px solid #e5e7eb;
  cursor: pointer;
  margin-left: 0.5rem;
}

.cancel-btn:hover {
  background-color: #e5e7eb;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: var(--background);
  padding: 2rem;
  border-radius: 0.5rem;
  width: 90%;
  max-width: 500px;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 1.5rem;
}

.user-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 500;
  color: var(--text-primary);
}

.form-input {
  padding: 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
}

.loading {
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary);
}

.error {
  background-color: #fee2e2;
  color: #991b1b;
  padding: 1rem;
  border-radius: 0.5rem;
  margin: 1rem 0;
  text-align: center;
}

.inline-input {
  padding: 0.25rem 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  width: 100%;
}

.inline-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.1);
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  line-height: 1.5;
  border-radius: 0.2rem;
}

.btn-sm i {
  font-size: 0.875rem;
}

.btn-primary:hover, .btn-success:hover, .btn-danger:hover, .btn-secondary:hover {
  opacity: 0.8;
}
</style> 